import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import { PageNotFound } from '@risksmart-app/components/src/errors/errors';
import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { IssueTaxonomyKeys } from '@risksmart-app/shared/forms/issues/types';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { ObjectToolbarConfig } from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import { useObjectToolbarFormStore } from 'src/blocks/object-toolbar/useObjectToolbarFormStore';
import ControlledTabs from 'src/components/controlled-tabs';
import DeleteModal from 'src/components/delete-modal/DeleteModal';
import TabSettingsModal from 'src/components/tab-settings-modal';
import { PageLayout } from 'src/layouts';
import { Permission } from 'src/rbac/Permission';
import {
  useGetDetailParentPath,
  useGetDetailPath,
} from 'src/routes/useGetDetailParentPath';

import { useDeleteAssets } from '@/hooks/mutations/asset';
import { useGetAssetById } from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useDeleteResultNotification } from '@/hooks/useMutationResultNotification';
import useTabs from '@/hooks/useTabs';
import { getFriendlyId } from '@/utils/friendlyId';

import useExporter from './useExporter';

type Props = {
  activeTabId:
    | 'actions'
    | 'details'
    | 'linkedItems'
    | 'notificationHistory'
    | IssueTaxonomyKeys;
  showDeleteButton?: boolean;
};

const Page: FC<Props> = ({ activeTabId, showDeleteButton }) => {
  const { t: st } = useTranslation(['common'], { keyPrefix: 'assets' });
  const { t } = useTranslation(['common']);
  const objectToolbarEnabled = useIsFeatureFlagEnabled('dynamic_forms');
  const defaultTitle = st('fallback_title');
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [showTabSettings, setShowTabSettings] = useState(false);
  // Form-editor opener bridged up from the active tab's FormMenu (relocate mode).
  // Present only on the details tab when dynamic_forms are on.
  const formConfig = useObjectToolbarFormStore((s) => s.formConfigState);
  const navigate = useNavigate();
  const assetId = useGetGuidParam('assetId');
  const [exportItem, { loading: exporting }] = useExporter(assetId);
  const parentPath = useGetDetailParentPath(assetId);
  const detailsPath = useGetDetailPath(assetId);

  const { data, error } = useGetAssetById({ queryArgs: { id: assetId } });
  if (error) {
    throw error;
  }

  const { deleteAssets, loading: deleteLoading } = useDeleteAssets();

  const asset = data?.asset?.[0];
  const tabs = useTabs({
    parentType: Parent_Type_Enum.Asset,
    parent: asset,
    hrefRoot: detailsPath,
  });

  if (data?.asset.length === 0) {
    throw new PageNotFound(`Asset with id ${assetId} not found`);
  }

  const onDelete = useDeleteResultNotification({
    entityName: st('entity_name'),
    asyncAction: async () => {
      if (!asset) {
        return false;
      }
      await deleteAssets({ Ids: [asset.Id] });
      navigate(parentPath);

      return true;
    },
  });

  const title = asset?.Title || defaultTitle;
  const friendlyId =
    asset && getFriendlyId(Parent_Type_Enum.Asset, asset.SequentialId);
  const counter = friendlyId && `(${friendlyId})`;

  // Object-level toolbar (CUS-157) — flag-gated. Relocates Export + Delete into
  // the toolbar's overflow ⋮ menu, hosts the relocated Edit form / Edit tabs
  // actions, and surfaces the Save disc / Cancel X (save state bridged from the
  // active tab's form via useObjectToolbarFormStore).
  const toolbarConfig: ObjectToolbarConfig = useMemo(
    () => ({
      header: {
        title,
        counter: friendlyId || undefined,
      },
      parentObject: asset,
      save: true,
      actions: {
        overflow: [
          {
            id: 'edit',
            label: t('edit'),
            actions: [
              ...(formConfig
                ? [
                    {
                      id: 'edit-form',
                      label: t('edit_form'),
                      iconName: 'file-06' as const,
                      onClick: formConfig.openConfigure,
                    },
                  ]
                : []),
              {
                id: 'edit-tabs',
                label: t('edit_tabs'),
                iconName: 'layers-three-01' as const,
                onClick: () => setShowTabSettings(true),
              },
            ],
          },
        ],
        export: [
          {
            id: 'export',
            actions: [
              {
                id: 'export',
                label: t('export.standard_pdf'),
                iconName: 'download-01',
                onClick: exportItem,
                disabled: exporting,
              },
            ],
          },
        ],
        ...(showDeleteButton
          ? {
              danger: [
                {
                  id: 'delete',
                  label: st('delete_button'),
                  iconName: 'trash-01' as const,
                  permission: 'delete:asset' as const,
                  // Wait for the entity to hydrate. `onDelete` guards on
                  // `if (!asset) return false`, which surfaces as a bare
                  // "Failed to save changes" with NO request issued, so a click
                  // inside the hydration window looks like a broken delete
                  // (ENG-616). Disabling also restores Playwright's actionability
                  // wait for the e2e suite.
                  disabled: !asset,
                  onClick: () => setIsDeleteModalVisible(true),
                },
              ],
            }
          : {}),
      },
    }),
    [
      title,
      friendlyId,
      asset,
      t,
      st,
      exportItem,
      exporting,
      formConfig,
      showDeleteButton,
    ]
  );

  return (
    <PageLayout
      title={title}
      meta={{
        title: defaultTitle,
      }}
      counter={counter}
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar config={toolbarConfig} />
        ) : undefined
      }
      actions={
        <SpaceBetween direction={'horizontal'} size={'xs'}>
          <Button
            iconName={'download'}
            disabled={exporting}
            onClick={exportItem}
          >
            {t('export.export')}
          </Button>
          {showDeleteButton && (
            <Permission permission={'delete:asset'} parentObject={asset}>
              <Button
                variant={'normal'}
                formAction={'none'}
                // Hydration gate — see the toolbar danger action above.
                disabled={!asset}
                onClick={() => {
                  setIsDeleteModalVisible(true);
                }}
              >
                {st('delete_button')}
              </Button>
            </Permission>
          )}
        </SpaceBetween>
      }
    >
      <ControlledTabs
        activeTabId={activeTabId}
        tabs={tabs}
        variant={'container'}
        parentType={Parent_Type_Enum.Asset}
        parent={asset}
        disableSettings={objectToolbarEnabled}
      />
      <TabSettingsModal
        parentType={Parent_Type_Enum.Asset}
        isVisible={showTabSettings}
        onDismiss={() => setShowTabSettings(false)}
        parent={asset}
      />
      <DeleteModal
        loading={deleteLoading}
        isVisible={isDeleteModalVisible}
        header={t('delete')}
        onDelete={onDelete}
        onDismiss={() => setIsDeleteModalVisible(false)}
      >
        {st('confirm_delete_message')}
      </DeleteModal>
    </PageLayout>
  );
};

export default Page;
